import React, { useState, useEffect, useRef } from 'react';
import { TextArea } from 'components/atoms';
import {
  AIVoiceSection,
  ToneOfVoiceDropdown,
  BackgroundMusicSection,
  AudioScriptPlaybackCard,
} from 'components/molecules';
import { InputField } from 'components/atoms/input-field';
import { Form } from 'react-final-form';
import {
  businessNameValidator,
  createMainMessageValidatorWithBackendCheck,
} from 'utils/gen-ai-audio-tool/validation';
import { useTranslation } from 'next-i18next';
import { useMixedVoiceOverMutation } from 'hooks/api/v3/useMixedVoiceOverMutation';
import {
  VoiceoverTone,
  GenerateMixedVoiceoverResponse,
  TextWithTimestamp,
  GenerateAdError,
  GenerateAdErrorNameEnum,
} from '@spotify-internal/ads-api-typescript-client-development';
import { VOICEOVER_TONE_MAP } from 'components/molecules/tone-of-voice-dropdown/constants';
import { VOICE_IDS } from 'components/molecules/ai-voice-section/constants';
import * as Styled from './GenAITool.styled';

export type GenAIToolFormValues = {
  businessName: string;
  mainMessage: string;
  toneOfVoice: string;
  aiVoice: string;
  backgroundMusic: string;
};

export enum GenerateButtonState {
  // eslint-disable-next-line no-unused-vars
  Idle = 'idle',
  // eslint-disable-next-line no-unused-vars
  Generating = 'generating',
  // eslint-disable-next-line no-unused-vars
  Completed = 'completed',
}

export type AudioTrackInfo = {
  id: string;
  audioElement: HTMLAudioElement | null;
};

// Helper function to convert TextWithTimestamp array to string
const formatTranscript = (
  transcript: string | TextWithTimestamp[] | undefined,
): string => {
  if (!transcript) return '';
  if (typeof transcript === 'string') return transcript;
  return transcript.map(item => item.text).join(' ');
};

export const GenAITool = () => {
  const [buttonState, setButtonState] = useState<GenerateButtonState>(
    GenerateButtonState.Idle,
  );
  const [generatedAt, setGeneratedAt] = useState<Date | undefined>(undefined);
  const [
    audioResponse,
    setAudioResponse,
  ] = useState<GenerateMixedVoiceoverResponse | null>(null);
  const [generatedAdError, setGeneratedAdError] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [currentAudioTrack, setCurrentAudioTrack] = useState<AudioTrackInfo>({
    id: '',
    audioElement: null,
  });
  const formRef = useRef<any>(null);

  const { t } = useTranslation();

  // Effect to trigger validation when backend error changes
  useEffect(() => {
    if (generatedAdError && formRef.current) {
      const formState = formRef.current.getState();
      if (formState && formState.values && formState.values.mainMessage) {
        const currentValue = formState.values.mainMessage;
        formRef.current.batch(() => {
          formRef.current.change('mainMessage', `${currentValue} `); // change to empty string to trigger validation
          formRef.current.change('mainMessage', currentValue);
        });
      }
    }
  }, [generatedAdError]);

  const {
    mutate: generateMixedVoiceOver,
    isPending: isGeneratingAudio,
  } = useMixedVoiceOverMutation();

  const getButtonText = () => {
    switch (buttonState) {
      case GenerateButtonState.Generating:
        return 'Generating...';
      case GenerateButtonState.Completed:
        return 'Regenerate';
      default:
        return 'Generate';
    }
  };

  // Helper function to format error messages from backend
  const formatErrorMessage = (errors: GenerateAdError[]): string => {
    if (!errors || errors.length === 0) return '';

    // Format error message based on error type
    const error = errors[0];

    switch (error.name) {
      case GenerateAdErrorNameEnum.ScriptGenerationFailed:
        return "Couldn't generate scripts. Include more details about your message and try again.";
      case GenerateAdErrorNameEnum.ScriptOffensive:
      case GenerateAdErrorNameEnum.ScriptGuardrailTopic:
        return 'Audio generation for this topic is restricted in this demo. <a href="https://adshelp.spotify.com/s/article/advertising-policies-US?language=en_US" target="_blank" rel="noopener noreferrer" style="color: #e91429; text-decoration: underline;">Learn more about category restrictions</a>';
      default:
        return '';
    }
  };

  const handleGenerate = async (values: GenAIToolFormValues) => {
    setButtonState(GenerateButtonState.Generating);
    setGeneratedAdError(null); // Clear previous errors
    setErrorBanner(null); // Clear previous error banner

    if (
      currentAudioTrack.id === 'script' &&
      currentAudioTrack.audioElement &&
      !currentAudioTrack.audioElement.paused
    ) {
      currentAudioTrack.audioElement.currentTime = 0;
      currentAudioTrack.audioElement.pause();
    }

    generateMixedVoiceOver(
      {
        brandName: values.businessName,
        talkingPoints: values.mainMessage,
        aiVoiceoverTone:
          VOICEOVER_TONE_MAP[values.toneOfVoice] ||
          VoiceoverTone.Conversational,
        voiceProfileId: values.aiVoice,
        backgroundId: values.backgroundMusic,
      },
      {
        onSuccess: data => {
          // Check if response contains errors
          if (data.errors && data.errors.length > 0) {
            const generatedAdErrorMessages = formatErrorMessage(data.errors);
            if (generatedAdErrorMessages) {
              setGeneratedAdError(generatedAdErrorMessages); // for Retricted content
            } else {
              setErrorBanner(t('Something went wrong. Please try again.'));
            }
            setButtonState(GenerateButtonState.Idle);
          } else {
            setAudioResponse(data);
            setButtonState(GenerateButtonState.Completed);
            setGeneratedAt(new Date());
          }
        },
        onError: () => {
          setButtonState(GenerateButtonState.Idle);
          setErrorBanner(t('Something went wrong. Please try again.'));
        },
      },
    );
  };

  const onSubmit = (values: GenAIToolFormValues) => {
    handleGenerate(values);
  };

  const initialValues: GenAIToolFormValues = {
    businessName: '',
    mainMessage: '',
    toneOfVoice: 'conversational_friendly',
    aiVoice: VOICE_IDS.ADDISON,
    backgroundMusic: 'b420ff18-1cf6-447e-9f0c-9eaf46279adb',
  };

  // Get audio URL from response
  const audioUrl = audioResponse?.mixUrl || '';

  // Convert transcript to string format for AudioScriptPlaybackCard
  const script = formatTranscript(audioResponse?.transcript);

  // Create validator that includes backend errors when needed
  const mainMessageValidatorWithBackendError = createMainMessageValidatorWithBackendCheck(
    generatedAdError,
    () => setGeneratedAdError(null),
  );

  const handlePlayAudio = ({
    id,
    audioElement,
    playFromStart = false,
  }: Pick<AudioTrackInfo, 'id' | 'audioElement'> & {
    playFromStart?: boolean;
  }) => {
    // Stop any currently playing audio
    if (currentAudioTrack?.id !== id && currentAudioTrack?.audioElement) {
      currentAudioTrack.audioElement.pause();
      currentAudioTrack.audioElement.currentTime = 0;
    }
    // Start playing the new audio
    if (playFromStart && audioElement) {
      // eslint-disable-next-line no-param-reassign
      audioElement.currentTime = 0;
    }
    audioElement?.play();
    setCurrentAudioTrack({ id, audioElement });
  };

  const handlePauseAudio = () => {
    if (currentAudioTrack?.audioElement) {
      currentAudioTrack.audioElement.pause();
      setCurrentAudioTrack(currentAudioTrackState => ({
        ...currentAudioTrackState,
      }));
    }
  };

  const handleAudioEnded = () => {
    setCurrentAudioTrack(currentAudioTrackState => ({
      ...currentAudioTrackState,
    }));
  };

  return (
    <Styled.Container>
      <Styled.Content>
        <Form<GenAIToolFormValues>
          onSubmit={onSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, form }) => {
            // Store form reference
            formRef.current = form;

            return (
              <Styled.Background>
                {errorBanner && (
                  <Styled.ErrorBanner>
                    <Styled.ErrorIcon name="form-error" />
                    <Styled.ErrorMessage>
                      {t('Something went wrong. Please try again.')}
                    </Styled.ErrorMessage>
                  </Styled.ErrorBanner>
                )}
                <Styled.ContentBackground>
                  <form onSubmit={handleSubmit}>
                    <Styled.FormInputs>
                      <Styled.ScrollableFormInputs>
                        <Styled.MessageField>
                          <InputField
                            name="businessName"
                            fieldName="businessName"
                            placeholder="Business Name"
                            validator={businessNameValidator(t)}
                          />
                        </Styled.MessageField>
                        <Styled.MessageField>
                          <TextArea
                            name="mainMessage"
                            fieldName="mainMessage"
                            placeholder="Main message"
                            validator={mainMessageValidatorWithBackendError(t)}
                          />
                          <Styled.MessageFieldLabel>
                            Describe the core message of your audio in 1-2
                            sentences.
                          </Styled.MessageFieldLabel>
                        </Styled.MessageField>
                        <ToneOfVoiceDropdown fieldName="toneOfVoice" />
                        <AIVoiceSection
                          fieldName="aiVoice"
                          handlePlayAudio={handlePlayAudio}
                          handlePauseAudio={handlePauseAudio}
                          handleAudioEnded={handleAudioEnded}
                          currentAudioId={currentAudioTrack.id}
                        />
                        <BackgroundMusicSection
                          fieldName="backgroundMusic"
                          handlePlayAudio={handlePlayAudio}
                          handlePauseAudio={handlePauseAudio}
                          handleAudioEnded={handleAudioEnded}
                          currentAudioId={currentAudioTrack.id}
                        />
                      </Styled.ScrollableFormInputs>
                      <Styled.GenerateButton
                        type="submit"
                        disabled={
                          buttonState === GenerateButtonState.Generating ||
                          isGeneratingAudio
                        }
                      >
                        {getButtonText()}
                      </Styled.GenerateButton>
                    </Styled.FormInputs>
                  </form>
                  <Styled.Output>
                    <AudioScriptPlaybackCard
                      audioUrl={audioUrl}
                      script={script}
                      isLoading={isGeneratingAudio}
                      generatedAt={generatedAt}
                      handlePlayAudio={handlePlayAudio}
                      handlePauseAudio={handlePauseAudio}
                      handleAudioEnded={handleAudioEnded}
                      isPlaying={
                        currentAudioTrack.id === 'script' &&
                        !!currentAudioTrack.audioElement &&
                        !currentAudioTrack.audioElement.paused
                      }
                    />
                  </Styled.Output>
                </Styled.ContentBackground>
              </Styled.Background>
            );
          }}
        />
      </Styled.Content>
    </Styled.Container>
  );
};
